import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { User, ArrowRight, Loader2 } from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';

export default function ProfileSetupPage() {
  const { customer, updateProfile, logout } = useCustomerAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState(customer?.name || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);

    const result = await updateProfile({
      name: name.trim(),
      mobile: customer?.mobile
    });

    if (result.success) {
      toast.success('Welcome to MAKS Play!');
      const qnext = new URLSearchParams(window.location.search).get('next');
      const redirect = qnext || localStorage.getItem('post_login_redirect');
      if (redirect) { localStorage.removeItem('post_login_redirect'); navigate(redirect); }
      else navigate('/');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <div className="p-4 sm:p-6 flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">M</span>
          </div>
          <span className="font-bold text-xl text-slate-800">MAKS SPORTS INFRA & TECH LLP</span>
        </div>
        <button 
          onClick={handleLogout}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Sign out
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-4">
              <User className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              What's your name?
            </h1>
            <p className="text-slate-600">
              Let's personalize your booking experience
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 font-medium text-base">
                  Your Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  className="h-14 text-lg px-4 border-2 border-slate-200 focus:border-emerald-500"
                  data-testid="name-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !name.trim()}
                className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold text-lg rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue to Book
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-center text-xs text-slate-400 mt-4">
              You can add more details in your profile later
            </p>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <CustomerFooter />
    </div>
  );
}
